import { SubtaskDto } from '@sib/api';
import { useTranslation } from '@sib/i18n';
import {
  Button,
  Form,
  FormSwitch,
  FormTextarea,
  FormTextField,
  FormToggleGroup,
} from '@sib/ui';
import { usePriorityOptions } from '@sib/ui/hooks';
import { SubtaskField, useSubtaskForm } from './SubtaskForm.utils';

interface SubtaskFormProps {
  taskId: string;
  /** Subtask to edit. When omitted, the form creates a new subtask. */
  subtask?: SubtaskDto;
  onGoBack: () => void;
}

export const SubtaskForm = ({
  taskId,
  subtask,
  onGoBack,
}: SubtaskFormProps) => {
  const { t } = useTranslation();
  const { formProps, onSubmit, isPending, isValid, isDirty } = useSubtaskForm({
    taskId,
    subtask,
    onGoBack,
  });
  const isEditing = Boolean(subtask);
  const priorityOptions = usePriorityOptions();

  return (
    <Form
      {...formProps}
      onSubmit={onSubmit}
      className="flex h-full w-full flex-col gap-6"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-primary text-lg">
            {isEditing
              ? t('components.taskDetails.subtasks.editSubtask.heading')
              : t('components.taskDetails.subtasks.newSubtask.heading')}
          </h3>
          <FormSwitch
            name={SubtaskField.Completed}
            label={t(
              'components.taskDetails.subtasks.newSubtask.markAsComplete'
            )}
            disabled={isPending}
            labelProps={{
              className: 'text-primary cursor-pointer ml-2 text-sm font-medium',
            }}
          />
        </div>

        <FormTextField
          name={SubtaskField.Title}
          label={t('components.taskForm.fields.title.label')}
          placeholder={t('components.taskForm.fields.title.placeholder')}
          disabled={isPending}
        />

        <FormTextarea
          name={SubtaskField.Description}
          label={t('components.taskForm.fields.description.label')}
          placeholder={t('components.taskForm.fields.description.placeholder')}
          rows={4}
          disabled={isPending}
          className="resize-none"
        />

        <FormToggleGroup
          type="single"
          label={t('components.actionItemForm.fields.priority')}
          className="h-12 w-full"
          name={SubtaskField.PriorityLevel}
          options={priorityOptions}
          groupItemsProps={{
            variant: 'chip',
          }}
          disabled={isPending}
        />
      </div>

      <div className="flex shrink-0 justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={isPending}
          onClick={onGoBack}
        >
          {t('cancel')}
        </Button>
        <Button
          type="submit"
          disabled={!isValid || (isEditing && !isDirty)}
          loading={isPending}
        >
          {isEditing
            ? t('components.taskDetails.subtasks.editSubtask.saveChanges')
            : t('components.taskDetails.subtasks.newSubtask.createSubtask')}
        </Button>
      </div>
    </Form>
  );
};
